import { h, render } from 'preact';
import { modalIframeManager } from './utils/singleton-iframe';

const styles = {
  container: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
    zIndex: '10000'
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid transparent',
    borderTopColor: '#A86FF7',
    borderLeftColor: '#A86FF7',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

const keyframes = `
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingComponent = () => (
  <div style={styles.container}>
    <style>{keyframes}</style>
    <div style={styles.spinner}></div>
  </div>
);

/**
 * Creates a loading manager instance that interacts with the singleton iframe.
 * @returns An object with show, hide, and isLoading properties.
 */
export function createLoading() {
  let _isLoading = false;
  let rootElement: HTMLElement | null = null;
  let showCount = 0;
  const hideTimeouts = new Set<ReturnType<typeof setTimeout>>();

  const show = async (options?: { duration?: number }) => {
    const wasLoading = _isLoading;
    showCount++;
    _isLoading = true;

    if (!wasLoading) {
      try {
        const { iframe } = await modalIframeManager.acquire();
        const doc = iframe.contentDocument;

        if (doc) {
          rootElement = doc.createElement('div');
          rootElement.id = 'loading-root';
          doc.body.appendChild(rootElement);
          render(<LoadingComponent />, rootElement);
        } else {
          throw new Error('Iframe document is not available.');
        }
      } catch (e) {
        console.error('[LoadingManager] Failed to show loading:', e);
        // Clean up on failure
        showCount = 0;
        _isLoading = false;
        modalIframeManager.release();
        return;
      }
    }

    if (options?.duration && typeof options.duration === 'number') {
      const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
        hide();
        hideTimeouts.delete(timer);
      }, options.duration);
      hideTimeouts.add(timer);
    }
  };

  const hide = () => {
    if (!_isLoading) {
      return;
    }

    showCount--;

    if (showCount <= 0) {
      showCount = 0;
      _isLoading = false;

      hideTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      hideTimeouts.clear();

      if (rootElement) {
        render(null, rootElement); // unmount component
        rootElement.parentNode?.removeChild(rootElement);
        rootElement = null;
      }
      modalIframeManager.release();
    }
  };

  return {
    show,
    hide,
    get isLoading() {
      return _isLoading;
    }
  };
}

export const loadingManager = createLoading();
